import apiClient from '../../../services/api/apiClient';

export type FilterOp = 'eq' | 'neq' | 'contains' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'isnull' | 'notnull';

export interface ReportFilter { field: string; op: FilterOp; value?: any; }

export interface ReportSpec {
  entity: string;
  title?: string;
  columns: string[];
  filters: ReportFilter[];
  groupBy?: string | null;
  aggregate?: string | null;
  sort?: { field: string; dir: 'asc' | 'desc' } | null;
  limit?: number;
}

export interface FieldMeta { type: string; label: string; values?: string[]; }
export interface EntityMeta { label: string; defaultColumns: string[]; fields: Record<string, FieldMeta>; }
export type ReportSchema = Record<string, EntityMeta>;

export interface RunResult {
  mode: 'table' | 'grouped';
  columns: string[];
  rows: Record<string, any>[];
  total: number;
  returned?: number;
  spec: ReportSpec;
  warnings?: string[];
}

export interface MonthlyMetric { key: string; label: string; value: number; total?: number | null; group?: string; }
export interface SummaryDrill {
  metric: string;
  label: string;
  range: { start: string; end: string };
  columns: string[];
  total: number;
  rows: Record<string, any>[];
}
export interface MonthlyBreakdownRow { key: string; count: number; }
export interface MonthlyRegionRow { key: string; total: number; buyer: number; partner: number; }
export interface EngagementRow { key: string; email: number; call: number; }
export type SummaryPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';
export interface MonthlySummary {
  period: SummaryPeriod;
  label: string;
  month: string;
  range: { start: string; end: string };
  metrics: MonthlyMetric[];
  breakdowns: {
    accountsByIndustry: MonthlyBreakdownRow[];
    accountsByRegion: MonthlyRegionRow[];
    engagementByIndustry?: EngagementRow[];
    engagementByRegion?: EngagementRow[];
  };
}

export const reportsService = {
  async getSchema(): Promise<ReportSchema> {
    const { data } = await apiClient.get('/api/reports/schema');
    return data.entities;
  },

  async generate(prompt: string): Promise<{ spec: ReportSpec; warnings: string[] }> {
    const { data } = await apiClient.post('/api/reports/generate', { prompt });
    return data;
  },

  async run(spec: ReportSpec): Promise<RunResult> {
    const { data } = await apiClient.post('/api/reports/run', { spec });
    return data;
  },

  async getPeriodSummary(period: string, date: string): Promise<MonthlySummary> {
    const { data } = await apiClient.get('/api/reports/monthly-summary', { params: { period, date } });
    return data;
  },

  async getSummaryDrill(metric: string, period: string, date: string): Promise<SummaryDrill> {
    const { data } = await apiClient.get('/api/reports/summary-drill', { params: { metric, period, date } });
    return data;
  },

  async exportCsv(spec: ReportSpec): Promise<void> {
    const res = await apiClient.post('/api/reports/export', { spec }, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    const name = (spec.title || spec.entity || 'report').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
    a.download = `${name}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
